import React, { HTMLAttributes, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { classNames } from '../../utilities';
import './Slideout.scss';
import { CloseButton } from '../Button';
import { Portal } from '../Portal';
import { FocusLock } from '../FocusLock';
import useOutsideClick from '../../utilities/hooks/use-outside-click';

export interface SlideoutProps extends HTMLAttributes<HTMLElement> {
  /** Accessibility label for screen readers */
  ['aria-labelledby']?: string;
  /* Attach a custom class to the slideout container */
  className?: string;
  /** Reference to whatever opened up the slideout */
  finalFocusRef?: React.RefObject<HTMLElement>;
  /** Passed in Footer as a React Node */
  footer?: React.ReactNode;
  /** Passed in Header as a React Node */
  header: React.ReactNode;
  /** Whether to show slideout */
  isOpen?: boolean;
  /** Reference to element to focus on when slideout is opened */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** Classname to apply to mask/overlay */
  maskClassName?: string;
  /** Whether to show overlay or still allow clicking on page */
  showOverlay?: boolean;
  /** Width of the slideout. 450px - 900px - 1100px */
  size?: 'small' | 'medium' | 'large';
  /** Whether to sticky the footer or not */
  stickyFooter?: boolean;
  /** Whether or not to have the header portion stickied */
  stickyHeader?: boolean;
  /** Callback when slideout is closed */
  onClose?: () => void;
}

/**
 * Generic Slideout Component
 */
export const Slideout = (props: SlideoutProps) => {
  const {
    className,
    maskClassName,
    finalFocusRef,
    footer,
    header,
    isOpen,
    initialFocusRef,
    showOverlay = true,
    size = 'small',
    stickyHeader,
    stickyFooter,
    onClose,
  } = props;

  if (stickyHeader && !header) {
    throw new Error('Header should be defined if stickyHeader prop is passed in');
  }
  if (stickyFooter && !footer) {
    throw new Error('Footer should be defined if stickyHeader prop is passed in');
  }

  const [showSlideout, setShowSlideout] = useState(false);
  /** This controls triggering entering/exiting animations */
  const [showMask, setShowMask] = useState(false);

  const slideoutRef = useRef<HTMLDivElement>(null);

  useOutsideClick(slideoutRef, () => showOverlay && setShowMask(false));

  const handleEscape = (e: KeyboardEvent) => e.key === 'Escape' && setShowMask(false);

  // * Handles escape key press
  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => {
      document?.body?.classList.remove('body--slideout-mask');
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // * Handles showing/hiding slideout
  useEffect(() => {
    if (!isOpen) {
      // slideout closing
      document?.body?.classList.remove('body--slideout-open');
      if (showOverlay) {
        document?.body?.classList.remove('body--slideout-mask');
      }
      // Exit animations need to play first before we hide slideout
      setShowMask(false);
    } else {
      // slideout opening
      document?.body?.classList.add('body--slideout-open');
      if (showOverlay) {
        document?.body?.classList.add('body--slideout-mask');
      }
      setShowSlideout(true);
      setShowMask(true);
    }
  }, [isOpen, showOverlay]);

  const hideSlideout = () => setShowMask(false);

  const handleAnimationFinish = () => {
    if (!showMask) {
      onClose && onClose();
      setShowSlideout(false);
    }
  };

  /** Sets the necessary scrollbox to handle drop-shadow */
  const getScrollBox = useCallback(
    (header: ReactNode, footer: ReactNode) => {
      const wrappedHeader = (
        <header className={classNames('ps-slideout__header', stickyHeader ? 'ps-slideout__header--sticky' : '')}>
          {header}
        </header>
      );
      const wrappedFooter = footer && (
        <footer className={classNames('ps-slideout__footer', stickyFooter ? 'ps-slideout__footer--sticky' : '')}>
          {footer}
        </footer>
      );
      const wrappedBody = <div className="ps-slideout__body">{props.children}</div>;
      if (stickyHeader && !stickyFooter) {
        return (
          <>
            {wrappedHeader}
            <div className="ps-slideout__scrollbox">
              {wrappedBody}
              {wrappedFooter}
            </div>
          </>
        );
      } else if (!stickyHeader && stickyFooter) {
        return (
          <>
            <div className="ps-slideout__scrollbox">
              {wrappedHeader}
              {wrappedBody}
            </div>
            {wrappedFooter}
          </>
        );
      } else if (stickyHeader && stickyFooter) {
        return (
          <>
            {wrappedHeader}
            <div className="ps-slideout__scrollbox">{wrappedBody}</div>
            {wrappedFooter}
          </>
        );
      } else {
        return (
          <>
            {wrappedHeader}
            {wrappedBody}
            {wrappedFooter}
          </>
        );
      }
    },
    [stickyHeader, stickyFooter, props.children]
  );

  const slideoutClassNames = classNames(
    className ?? '',
    'aspect-base',
    'ps-slideout',
    `ps-slideout--${size}`,
    showOverlay ? '' : 'ps-slideout--has-overlay',
    showMask ? 'slide-from-right' : '',
    !showMask ? 'slide-out-right' : ''
  );

  const maskClassNames = classNames(
    maskClassName,
    'ps-slideout__mask',
    showMask ? 'ps-slideout__mask--fadein' : '',
    !showMask ? 'ps-slideout__mask--fadeout' : ''
  );

  const slideoutContent = getScrollBox(header, footer);

  return (
    <>
      {showSlideout && (
        <Portal id="ps-slideout-id">
          <div className={slideoutClassNames}>
            <FocusLock
              isDisabled={!showOverlay}
              autoFocus={false}
              restoreFocus={showOverlay}
              finalFocusRef={finalFocusRef}
              initialFocusRef={initialFocusRef}
            >
              <div
                ref={slideoutRef}
                className="ps-slideout__container"
                onAnimationEnd={handleAnimationFinish}
                aria-labelledby={props['aria-labelledby']}
                data-autofocus
              >
                <CloseButton className="ps-slideout__close-button" onClick={hideSlideout} />
                {slideoutContent}
              </div>
              {showOverlay && <div className={maskClassNames} />}
            </FocusLock>
          </div>
        </Portal>
      )}
    </>
  );
};
